/**
 * 服务器监控（root/admin 只读）query hooks — GET /api/v1/server-monitor/{overview,logs,user-crawl}。
 *
 * overview 每 60s 自动刷新（后端健康聚合由 cron 每 2 分钟预算进 KV，过期先返回旧值并后台刷新）；service 为 null 时不带参数，由后端取本环境目标，
 * 响应的 service / viewable_services 决定页面看哪个环境与能否切换。logs 按筛选 + cursor 分 key，
 * Telemetry 无总数，页面用 cursor 栈翻页。未配置 / Telemetry 故障时 logs 返 503，页面结合
 * overview.health.state 区分「未配置」与「查询失败」。经 `~/data/queries` 再导出。
 */

import type {
  ServerMonitorCategory,
  ServerMonitorLogLevel,
  ServerMonitorLogRange,
  ServerMonitorLogsResponse,
  ServerMonitorOverview,
  ServerMonitorService,
  ServerMonitorUserCrawl,
} from '@novel-admin/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiRequest } from '~/api/client';

export const SERVER_MONITOR_OVERVIEW_REFRESH_MS = 60_000;

export function useServerMonitorOverview(service: ServerMonitorService | null) {
  return useQuery({
    queryKey: ['server-monitor', 'overview', service ?? 'default'],
    retry: false,
    refetchInterval: SERVER_MONITOR_OVERVIEW_REFRESH_MS,
    // @real — GET /api/v1/server-monitor/overview[?service=]
    queryFn: () =>
      apiRequest<ServerMonitorOverview>({
        method: 'GET',
        path: '/api/v1/server-monitor/overview',
        query: service ? { service } : {},
      }),
  });
}

export type ServerMonitorLogsParams = {
  /** null = 还不知道本环境目标（等 overview 返回），不发请求 */
  service: ServerMonitorService | null;
  range: ServerMonitorLogRange;
  levels: ServerMonitorLogLevel[];
  categories: ServerMonitorCategory[];
  q: string;
  cursor: string | null;
};

export function useServerMonitorLogs(params: ServerMonitorLogsParams) {
  const levels = [...params.levels].sort();
  const categories = [...params.categories].sort();
  const q = params.q.trim();
  return useQuery({
    queryKey: [
      'server-monitor',
      'logs',
      params.service,
      params.range,
      levels.join(','),
      categories.join(','),
      q,
      params.cursor ?? '',
    ],
    retry: false,
    enabled: params.service !== null,
    placeholderData: keepPreviousData,
    // @real — GET /api/v1/server-monitor/logs?service&range&level&category&q&cursor
    queryFn: () =>
      apiRequest<ServerMonitorLogsResponse>({
        method: 'GET',
        path: '/api/v1/server-monitor/logs',
        query: {
          service: params.service as ServerMonitorService,
          range: params.range,
          ...(levels.length > 0 ? { level: levels.join(',') } : {}),
          ...(categories.length > 0 ? { category: categories.join(',') } : {}),
          ...(q ? { q } : {}),
          ...(params.cursor ? { cursor: params.cursor } : {}),
        },
      }),
  });
}

/** 疑似爬取命中用户（cron 每 2 分钟写 KV），与 overview 同频刷新；service 为 null 时由后端取本环境目标 */
export function useServerMonitorUserCrawl(service: ServerMonitorService | null) {
  return useQuery({
    queryKey: ['server-monitor', 'user-crawl', service ?? 'default'],
    retry: false,
    refetchInterval: SERVER_MONITOR_OVERVIEW_REFRESH_MS,
    // @real — GET /api/v1/server-monitor/user-crawl[?service=]
    queryFn: () =>
      apiRequest<ServerMonitorUserCrawl>({
        method: 'GET',
        path: '/api/v1/server-monitor/user-crawl',
        query: service ? { service } : {},
      }),
  });
}
